import { create } from 'zustand'

import {
  listConversations,
  createConversation,
  getConversationMessages,
  sendMessage,
  getSuggestions,
  getDashboardInsights,
  listAgentRuns,
  approveRun,
  rejectRun,
} from '../api/aiService'
import type {
  AIConversation,
  AIMessage,
  AISuggestion,
  AgentRun,
} from '../api/aiService'

interface AIState {
  conversations: AIConversation[]
  currentConversation: AIConversation | null
  messages: AIMessage[]
  isLoading: boolean
  isStreaming: boolean
  suggestions: AISuggestion[]
  pendingRuns: AgentRun[]
  insights: string[]
  summary: Record<string, unknown> | null
  error: string | null

  loadConversations: () => Promise<void>
  createNewConversation: (contextType?: string) => Promise<void>
  selectConversation: (conversation: AIConversation | null) => Promise<void>
  loadMessages: (conversationId: string) => Promise<void>
  sendChatMessage: (content: string) => Promise<void>
  loadSuggestions: () => Promise<void>
  loadInsights: () => Promise<void>
  loadPendingRuns: () => Promise<void>
  approveAgentRun: (runId: string) => Promise<void>
  rejectAgentRun: (runId: string) => Promise<void>
  clearError: () => void
}

export const useAIStore = create<AIState>((set, get) => ({
  conversations: [],
  currentConversation: null,
  messages: [],
  isLoading: false,
  isStreaming: false,
  suggestions: [],
  pendingRuns: [],
  insights: [],
  summary: null,
  error: null,

  loadConversations: async () => {
    try {
      const conversations = await listConversations()
      set({ conversations, error: null })
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to load conversations' })
    }
  },

  createNewConversation: async (contextType = 'general') => {
    try {
      const conversation = await createConversation('', contextType)
      set((s) => ({
        conversations: [conversation, ...s.conversations],
        currentConversation: conversation,
        messages: [],
        error: null,
      }))
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to create conversation' })
    }
  },

  selectConversation: async (conversation) => {
    set({ currentConversation: conversation, messages: [], error: null })
    if (conversation) {
      await get().loadMessages(conversation.id)
    }
  },

  loadMessages: async (conversationId) => {
    try {
      const messages = await getConversationMessages(conversationId)
      set({ messages, error: null })
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to load messages' })
    }
  },

  sendChatMessage: async (content) => {
    const { currentConversation } = get()
    if (!currentConversation) {
      // Auto-create conversation if none selected
      await get().createNewConversation()
      const newConv = get().currentConversation
      if (!newConv) {return}
    }

    const conv = get().currentConversation!
    const userMessage: AIMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content,
      created_at: new Date().toISOString(),
    }

    set((s) => ({
      messages: [...s.messages, userMessage],
      isLoading: true,
      error: null,
    }))

    try {
      const response = await sendMessage(conv.id, content)

      set((s) => ({
        messages: [...s.messages, response.message],
        isLoading: false,
      }))

      // Refresh conversation list to update title/order
      get().loadConversations()
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to get response',
      })
    }
  },

  loadSuggestions: async () => {
    try {
      const { suggestions } = await getSuggestions()
      set({ suggestions, error: null })
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to load suggestions' })
    }
  },

  loadInsights: async () => {
    try {
      const { summary, insights } = await getDashboardInsights()
      set({ summary, insights, error: null })
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to load insights' })
    }
  },

  loadPendingRuns: async () => {
    try {
      const runs = await listAgentRuns()
      set({ pendingRuns: runs.filter((r) => r.status === 'pending'), error: null })
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to load runs' })
    }
  },

  approveAgentRun: async (runId) => {
    try {
      await approveRun(runId)
      await get().loadPendingRuns()
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to approve run' })
    }
  },

  rejectAgentRun: async (runId) => {
    try {
      await rejectRun(runId)
      await get().loadPendingRuns()
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to reject run' })
    }
  },

  clearError: () => set({ error: null }),
}))
